import React, { useEffect, useRef } from 'react';

import { Link } from '../../atoms/Link/link';
import { Title } from '../../typography/title';
import { Paragraph } from '../../typography/paragraph';
import { Label } from '../../typography/label';
import { Input } from '../../atoms/Input/input';
import { ButtonPrimary } from '../../atoms/Button';

type SubscribeSectionProps = {
  heading?: string;
  subheading?: string;
  hasError?: boolean;
  hasSubscribed?: boolean;
  scrollToSection?: boolean;
  onSubmit: (email: string) => Promise<void>;
};

const DEFAULT_HEADING = "Don't miss a drop.";
const DEFAULT_SUBHEADING = 'Get first access to launches, creator updates, and exclusive offers.';

export default function SubscribeSection({
  heading = DEFAULT_HEADING,
  subheading = DEFAULT_SUBHEADING,
  hasError,
  hasSubscribed,
  scrollToSection = false,
  onSubmit,
}: SubscribeSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollToSection && sectionRef.current) {
      // Use double requestAnimationFrame for Safari compatibility
      // First rAF waits for layout, second waits for paint
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          sectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        });
      });
    }
  }, [scrollToSection]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const submitEmail = formData.get('email') as string;

    onSubmit(submitEmail);
  };

  return (
    <div
      ref={sectionRef}
      className="bg-[var(--color-primary-black)] py-16 sm:py-24 lg:py-32 dark:bg-[var(--color-primary-white)]"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 lg:grid-cols-12 lg:gap-8 lg:px-8">
        <div className="max-w-xl lg:col-span-7">
          <Title as="h2" size="large" color="primary-white">
            {heading}
          </Title>
          {subheading && (
            <Label size="large" color="primary-white" className="whitespace-pre-line">
              {subheading}
            </Label>
          )}
        </div>
        <form className="w-full max-w-md lg:col-span-5 lg:pt-2" onSubmit={handleSubmit}>
          {hasSubscribed && (
            <div className="mb-4">
              <Paragraph color="neutral-50">Thanks for subscribing!</Paragraph>
            </div>
          )}

          <div className="mb-4 flex gap-x-4">
            <Input
              id="email-address"
              name="email"
              type="email"
              placeholder="Enter your email"
              autoComplete="email"
              label="Email address"
              required
              textColor="primary-white"
            />
            <ButtonPrimary color="primary-blurple" type="submit">
              Subscribe
            </ButtonPrimary>
          </div>
          {hasError && (
            <div className="mb-4">
              <Paragraph size="small" color="red-600">
                An error with reCAPTCHA occurred. Please try again later.
              </Paragraph>
            </div>
          )}
          <Paragraph size="small" color="neutral-50">
            This site is protected by reCAPTCHA and the Google{' '}
            <Link color="light" href="https://policies.google.com/privacy" target="_blank">
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link color="light" href="https://policies.google.com/terms" target="_blank">
              Terms of Service
            </Link>{' '}
            apply. By subscribing, you agree to our{' '}
            <Link color="light" href="https://www.makeship.com/policies/privacy-policy" target="_blank">
              Privacy Policy
            </Link>
            .
          </Paragraph>
        </form>
      </div>
    </div>
  );
}
